/**
 * Design mode and the design system, where they are chosen: a chat's header
 * menu, and a project's page, whose choice its chats follow until they make
 * their own.
 */

import { useEffect, useRef, useState } from 'react';
import { listDesignSystems, type DesignSystem } from '../api';
import { ds } from '../ds';
import { useRouter } from '../router';
import { IconTap, Menu, MenuDivider, MenuItem, MonoLabel, useDismiss } from './ui';

/** The account's design systems, read when `active` turns on. */
export function useDesignSystems(active: boolean): DesignSystem[] | null {
  const [list, setList] = useState<DesignSystem[] | null>(null);
  useEffect(() => {
    if (!active) return;
    let gone = false;
    listDesignSystems().then(
      (r) => !gone && setList(r.designSystems),
      () => !gone && setList([])
    );
    return () => {
      gone = true;
    };
  }, [active]);
  return list;
}

const MENU_WIDTH = 260;

/** The chat header's control: design mode, and the design system. */
export function DesignMenu({
  design,
  designSystemId,
  inherited,
  inProject,
  onDesign,
  onSystem
}: {
  design: boolean;
  /** The chat's own; null follows its project's. */
  designSystemId: string | null;
  /** The project's, when the chat follows it. */
  inherited: string | null;
  inProject: boolean;
  onDesign: (on: boolean) => void;
  onSystem: (id: string | null) => void;
}) {
  const [open, setOpen] = useState<{ top: number; right: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, !!open, () => setOpen(null));
  const systems = useDesignSystems(!!open);
  const { navigate } = useRouter();
  const pick = (fn: () => void) => () => {
    setOpen(null);
    fn();
  };
  // Fixed to the window, so a narrow chat beside an artifact does not clip it.
  const toggle = () => {
    const r = ref.current?.getBoundingClientRect();
    setOpen(open || !r ? null : { top: r.bottom + 6, right: Math.min(window.innerWidth - r.right, window.innerWidth - MENU_WIDTH - 8) });
  };

  return (
    <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
      <IconTap icon="palette" label="Design mode" active={design} glyph={17} onClick={toggle} />
      {open ? (
        <Menu style={{ position: 'fixed', top: open.top, right: open.right, width: MENU_WIDTH, zIndex: 40 }}>
          <MenuItem icon="palette" label="Design mode" selected={design} onSelect={pick(() => onDesign(!design))} />
          <MenuDivider />
          <MonoLabel style={{ padding: '6px 9px 4px' }}>Design system</MonoLabel>
          <MenuItem
            label={inProject ? 'The project’s' : 'None'}
            {...(inherited ? { meta: inherited } : {})}
            selected={!designSystemId}
            onSelect={pick(() => onSystem(null))}
          />
          {(systems ?? []).map((s) => (
            <MenuItem key={s.id} label={s.name} selected={designSystemId === s.id} onSelect={pick(() => onSystem(s.id))} />
          ))}
          <MenuDivider />
          <MenuItem icon="gear-six" label="Design systems" onSelect={pick(() => navigate('/settings/design'))} />
        </Menu>
      ) : null}
    </div>
  );
}

/** A project's design mode and design system, for its chats. */
export function ProjectDesign({
  design,
  designSystemId,
  onChange
}: {
  design: boolean;
  designSystemId: string | null;
  onChange: (patch: { design?: boolean; designSystemId?: string | null }) => void;
}) {
  const { Switch, Select } = ds();
  const systems = useDesignSystems(true);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <MonoLabel>Design</MonoLabel>
      <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Switch label="Design mode" checked={design} onChange={(on) => onChange({ design: on })} />
        <Select
          label="Design system"
          value={designSystemId ?? ''}
          onChange={(e) => onChange({ designSystemId: e.target.value || null })}
          options={[{ value: '', label: 'None' }, ...(systems ?? []).map((s) => ({ value: s.id, label: s.name }))]}
        />
      </div>
    </div>
  );
}
